import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import { Formik } from 'formik';
import Cookies from 'universal-cookie';
import personalizationValidationSchema from '../Validation/PersonalizationValidation';
import {
  compareIncomingCrmDataToFormValues,
  updateNewResidentCookieAfterResidentUpdate,
  updateExistingResidentCookieAfterResidentUpdate,
  conditionallyPostCoupleToDrupal,
  conditionallyPatchCoupleInDrupal,
  processFormDataUsingIntent,
  createPersonAttributeDataUsingIntent,
  createInitialResidentCookieData,
  createInitialFavoritesCookieData,
  createInitialMediaFavoritesCookieData,
  createInitialResidenceFavoritesCookieData,
} from '../FormHelpers';
import FormNavButtons from './FormNavButtons';
import AccountLookupStep from '../FormSteps/AccountLookupStep';
import AccountNotFoundStep from '../FormSteps/AccountNotFoundStep';
import AccountFoundStep from '../FormSteps/AccountFoundStep';
import AccountResidentSelectionStep from '../FormSteps/AccountResidentSelectionStep';
import EndPersonalizationWarning from '../FormSteps/EndPersonalizationWarning';
import EndPersonalization from '../FormSteps/EndPersonalization';
import PersonalizeWelcomeMessage from '../FormSteps/PersonalizeWelcomeMessage';
import {
  fetchCrmPushStatus,
  fetchResidentsFromCrm,
  findExistingResidentInDrupal,
  patchResidentInDrupal,
} from '../../../../utils/DrupalEntityTypes/Resident/ResidentHelpers';
import {
  combineAndFormatYtSessionsForDrupal,
  removeYtCookies,
} from '../../PersonalizationHelpers';
import { useFavoriteButtons } from '../../Favorite/FavoriteButtonsContext';
import { useConfigStore, useAuthStore, useAppStore } from '../../../../stores';
import { toIsoString } from '../../../../utils/common';
import { personalizationDataShape } from '../formPropTypes';

const cookies = new Cookies();

// Stands in for the push status when it couldn't be fetched, shaped like one
// failed item so CrmPushStatusMessage renders it without a special case.
const crmPushStatusUnknown = (residentCookieData) => ({
  crm: null,
  pushed: false,
  items: [
    {
      resident_uuid: residentCookieData?.drupal_uuid || '',
      resident_name: [residentCookieData?.first_name, residentCookieData?.last_name]
        .filter(Boolean)
        .join(' ') || 'This session',
      type: 'status-unknown',
      ok: false,
      message: 'The session was saved, but we couldn\'t confirm whether it reached the CRM. '
        + 'Check the record there before relying on it.',
    },
  ],
});

// The free-text fields whose yup schema trims before validating. Formik uses
// the schema for validation only and discards the cast, so the trim has to be
// repeated here or the CRM lookup/create still receives the padded string.
const TRIMMED_FIELDS = [
  'email',
  'first_name',
  'last_name',
  'person2_email',
  'person2_first_name',
  'person2_last_name',
];

/**
 * Trim the free-text fields in place before a step's submit handler runs.
 *
 * Mutates the values object the rest of this form already writes back to, and
 * pushes each change into Formik state so the visible field matches what was
 * submitted.
 *
 * @param {Object} values
 *   Formik's form values.
 * @param {Object} actions
 *   Formik's form actions.
 */
const trimSubmittedValues = (values, actions) => {
  TRIMMED_FIELDS.forEach((field) => {
    const value = values[field];

    if (typeof value !== 'string') {
      return;
    }

    const trimmed = value.trim();

    if (trimmed === value) {
      return;
    }

    // eslint-disable-next-line no-param-reassign
    values[field] = trimmed;

    if (actions && typeof actions.setFieldValue === 'function') {
      actions.setFieldValue(field, trimmed);
    }
  });
};

const PersonalizationForm = ({
  beginSession,
  updateModalSize,
  closeModal,
  currentStep,
  setCurrentStep,
  setHasCRMResults,
  cookieDataDump,
  setCookieDataDump,
  setPersonalizationActive,
  isUpdatingProspect,
  setIsUpdatingProspect,
  goBackStep,
  cancelPersonalization,
}) => {
  const [crmDataState, setCrmDataState] = useState('');
  const [crmData, setCrmData] = useState([]);
  const [residentSelected, setResidentSelected] = useState('');
  const [dataIsSent, setDataIsSent] = useState(false);
  const [crmPushStatus, setCrmPushStatus] = useState(null);
  const [endSessionSaveError, setEndSessionSaveError] = useState(null);

  // We're doing '-1' here to line up with the array keys of the validation
  // array.
  const currentValidationSchema = personalizationValidationSchema[currentStep - 1];

  // Zustand stores
  const config = useConfigStore((state) => state.config);
  const crmConfig = useConfigStore((state) => state.crmConfig);
  const personalizationActive = useAppStore((state) => state.personalizationActive);
  const salespersonNid = useAuthStore((state) => state.salespersonNid);
  const salespersonId = useAuthStore((state) => state.salespersonId);
  const { syncFavoritesFromCookies } = useFavoriteButtons();

  const { urlSlug, communityUuid, communityNid } = config || {};

  const { machineName } = crmConfig || {};

  // Anytime personalization is stopped, clear out the state values.
  useEffect(() => {
    if (!personalizationActive) {
      setCrmDataState('');
      setCrmData([]);
      setResidentSelected('');
    }
    // crmPushStatus isn't cleared here: it's assigned as the session ends,
    // which is this same moment, and it's reassigned on every session end.
  }, [personalizationActive]);

  /**
   * Form submission functionality that begins personalization s.
   *
   * @param {Array} values
   *   Formik's form values array.
   * @param {Array} updatedFormData
   *   An array of any form data that has been updated after being returned
   *   from Drupal. This way, we know if updated data needs to be pushed back
   *   to Drupal.
   * @param {string} formIntent
   * @param {object} selectedResidentCrmData
   */
  async function submitPersonalization(
    values,
    updatedFormData,
    formIntent,
    selectedResidentCrmData,
  ) {
    // Format resident data to be sent to Drupal based on form changes.
    let {
      person1AttributesData,
      person2AttributesData,
    } = createPersonAttributeDataUsingIntent(
      values,
      formIntent,
      selectedResidentCrmData,
      updatedFormData,
    );

    // Change data according to intent.
    const {
      apiActions,
      uuids,
      updatedPerson2AttributesData,
    } = processFormDataUsingIntent(
      values,
      formIntent,
      person1AttributesData,
      person2AttributesData,
    );

    person2AttributesData = updatedPerson2AttributesData;

    // Gather the data and set the initial resident cookie.
    let residentCookieData = createInitialResidentCookieData(
      uuids,
      values,
      selectedResidentCrmData,
      urlSlug,
      salespersonNid,
    );

    // Gather the data and set the initial favorites cookie.
    const favoritesCookieData = createInitialFavoritesCookieData(
      selectedResidentCrmData,
    );
    cookies.set('favorites', favoritesCookieData, {
      path: '/',
      maxAge: 14400, // 4 hour cookie expiration.
      domain: window.location.hostname,
    });

    // Set the residence_favorites cookie.
    const residenceFavoritesCookieData = createInitialResidenceFavoritesCookieData(
      selectedResidentCrmData,
    );
    cookies.set('residence_favorites', residenceFavoritesCookieData, {
      path: '/',
      maxAge: 14400, // 4 hour cookie expiration.
      domain: window.location.hostname,
    });

    // Gather the data and set the initial media favorites cookie.
    const mediaFavoritesCookieData = createInitialMediaFavoritesCookieData(
      selectedResidentCrmData,
    );
    cookies.set('media_favorites', mediaFavoritesCookieData, {
      path: '/',
      maxAge: 14400, // 4 hour cookie expiration.
      domain: window.location.hostname,
    });

    // Push the freshly-seeded favorites into the context now so the new brochure
    // reflects this prospect's favorites instead of the previous prospect's.
    syncFavoritesFromCookies();

    // Add all sessions to data that will update Drupal.
    // Don't patch other CRMs - we'll do that at the end of personalization.
    if (machineName === 'drupal') {
      // Combine current and past yt sessions and format them for Drupal API.
      const formattedSessions = await combineAndFormatYtSessionsForDrupal(
        residentCookieData,
      );
      person1AttributesData = Object.assign(
        person1AttributesData,
        formattedSessions,
      );
    }

    // If we're posting Person 1, the session start will be sent with the post anyway.
    // If we aren't posting Person 1, then we need to patch them with the new start time.
    if (!apiActions.postPerson1 && machineName === 'drupal') {
      apiActions.patchPerson1 = true;
    }

    // Post individual to Drupal if the api action says to. This should only
    // happen for people with Drupal as their CRM.
    const newResidents = await conditionallyPostCoupleToDrupal(
      person1AttributesData,
      person2AttributesData,
      apiActions,
      communityUuid,
      salespersonId,
      values.crm_prospect_id,
    );

    // Update data based on posting above changes.
    if (newResidents && newResidents[0] && newResidents[0].data) {
      const person1Id = newResidents[0].data.id;
      uuids.person1 = person1Id;
      uuids.person2Spouse = person1Id;

      const newYtSession = newResidents[0]?.data?.attributes?.field_yt_sessions[0];
      if (newYtSession) {
        residentCookieData.yourtour_active_session = newYtSession;
        const formattedSessions = await combineAndFormatYtSessionsForDrupal(
          residentCookieData,
        );
        person1AttributesData = Object.assign(
          person1AttributesData,
          formattedSessions,
        );
      }
    }

    if (newResidents && newResidents[1] && newResidents[1].data) {
      const person2Id = newResidents[1].data.id;
      uuids.person2 = person2Id;
      uuids.person1Spouse = person2Id;
    }

    // Update individuals with changes. This should only happen for people with
    // Drupal as their CRM.
    const patchedResidents = await conditionallyPatchCoupleInDrupal(
      person1AttributesData,
      person2AttributesData,
      uuids,
      apiActions,
    );

    // For new residents, update cookie if there are cookie-related changes.
    if (
      newResidents?.length > 0
      && (!residentCookieData.drupal_uuid
        || residentCookieData.drupal_uuid === ''
        || residentCookieData.my_favorites_page === '')
    ) {
      // Create new cookie data from updated residents.
      residentCookieData = updateNewResidentCookieAfterResidentUpdate(
        newResidents,
        residentCookieData,
        urlSlug,
      );
    } else if (machineName === 'drupal') {
      // For existing residents, we need to add the current session's newly
      // created uuid to the cookie. We only do this for Drupal because only
      // residents with that CRM have been added to Drupal yet.
      residentCookieData = updateExistingResidentCookieAfterResidentUpdate(
        patchedResidents,
        residentCookieData,
      );
    }

    // Set new resident cookie.
    cookies.set('resident', residentCookieData, {
      path: '/',
      maxAge: 14400, // 4 hour cookie expiration.
      domain: window.location.hostname,
    });

    beginSession();
    closeModal();
  }

  /**
   * Step 1 submit.
   *
   * @param {Array} values
   * @param {Object} actions
   */
  async function step1Submit(values, actions) {
    // If we're on step 1 when the form is submitted, submit the user entered
    // to CRM and retrieve resident info.
    updateModalSize('small');

    // Step 1 of the form which look in CRM for a resident that matches
    // the first name, last name, and/or email address entered.
    const incomingCrmData = await fetchResidentsFromCrm(
      machineName,
      values.email,
      values.first_name,
      values.last_name,
      urlSlug,
      communityNid,
    );

    // If record is found.
    if (incomingCrmData?.length) {
      // When we find only one record based on the user's form entry.
      if (incomingCrmData?.length === 1) {
        const userData = incomingCrmData[0];
        actions.setFieldValue('drupal_uuid', userData?.drupalUuid);
        actions.setFieldValue('crm_individual_id', userData?.crmIndividualId);
        actions.setFieldValue('crm_prospect_id', userData?.crmProspectId);
        actions.setFieldValue('first_name', userData?.firstName ?? '');
        actions.setFieldValue('last_name', userData?.lastName ?? '');
        actions.setFieldValue('email', userData?.email ?? '');
        actions.setFieldValue('my_favorites_page', userData?.myFavoritesPage);
        actions.setFieldValue(
          'person2_drupal_uuid',
          userData?.person2DrupalUuid,
        );
        actions.setFieldValue(
          'person2_crm_individual_id',
          userData?.person2CrmIndividualId,
        );
        actions.setFieldValue(
          'person2_first_name',
          userData?.person2FirstName ?? '',
        );
        actions.setFieldValue(
          'person2_last_name',
          userData?.person2LastName ?? '',
        );
        actions.setFieldValue('person2_email', userData?.person2Email ?? '');

        // Found a record & drupal returned data for the user.
        if (userData.firstName || userData.lastName || userData.email) {
          setCrmDataState('found');
          // Advance to next form step.
          setCurrentStep(currentStep + 1);
        } else {
          // Found Record but crm has no data for the user.
          setCrmDataState('foundNoInfo');
          setResidentSelected('');
          // Advance to next form step.
          setCurrentStep(currentStep + 1);
        }
      } else {
        // Advance to next form step.
        setCurrentStep(currentStep + 1);
        setHasCRMResults(true);
      }
      setCrmData(incomingCrmData);
      updateModalSize('medium');
    } else {
      // Clear out existing data from previous searches.
      setCrmData([]);

      // Reset resident selected from previous searches.
      setResidentSelected('');

      // Did we not find anything because there weren't matches or because the
      // CRM isn't responding?
      // We didn't find any matching users in the CRM, so advance to the 'Create
      // new' form step.
      setCrmDataState('notFound');

      // Advance to next form step.
      setCurrentStep(3);

      // Populate the 'Create new' form with the data the user entered on step 1.
      actions.setFieldValue('first_name', values.first_name);
      actions.setFieldValue('last_name', values.last_name);
      actions.setFieldValue('email', values.email);

      updateModalSize('small');
    }

    // Reset these values to the next form isn't showing validation states.
    actions.setTouched({});
    actions.setSubmitting(false);
  }

  /**
   * Step 2 submit.
   *
   * @param {Array} values
   * @param {Object} actions
   */
  async function step2Submit(incomingValues, actions) {
    const values = incomingValues;

    // 'Or Create New Prospect' abandons the matches and goes to the edit step
    // with an empty person. This is CRM-agnostic: every CRM can create a
    // person, so the non-editable ones need this route as much as Drupal and
    // Enquire do — without it they'd fall through to the branch below and
    // start a session on a selection the user just cleared.
    if (values.action === 'create new') {
      actions.setFieldValue('drupal_uuid', '');
      actions.setFieldValue('first_name', '');
      actions.setFieldValue('last_name', '');
      actions.setFieldValue('email', '');
      actions.setFieldValue('my_favorites_page', '');
      actions.setFieldValue('person2_drupal_uuid', '');
      actions.setFieldValue('person2_first_name', '');
      actions.setFieldValue('person2_last_name', '');
      actions.setFieldValue('person2_email', '');
      actions.setFieldValue('resident', '');
      setResidentSelected('');

      // If the user got a result but they've decided to make a new person, we
      // need to reset this crmDataState or else the new person won't be submitted.
      setCrmDataState('notFound');

      // Advance to last form step.
      setCurrentStep(currentStep + 2);
      actions.setTouched({});
      actions.setSubmitting(false);
      return;
    }

    // If the Crm is Drupal or Enquire, let people edit the person.
    // Advance to the edit page.
    if (machineName === 'drupal' || machineName === 'enquire') {
      if (values.action === 'continue') {
        const selectedResident = crmData[values.residentSelection];
        // Set form fields with CRM data. If data doesn't exist, it will be a blank
        // field.
        if (selectedResident) {
          setCrmDataState('found');
          actions.setFieldValue('drupal_uuid', selectedResident.drupalUuid);
          actions.setFieldValue(
            'crm_individual_id',
            selectedResident.crmIndividualId,
          );
          actions.setFieldValue(
            'crm_prospect_id',
            selectedResident.crmProspectId,
          );
          actions.setFieldValue('first_name', selectedResident.firstName ?? '');
          actions.setFieldValue('last_name', selectedResident.lastName ?? '');
          actions.setFieldValue('email', selectedResident.email ?? '');
          actions.setFieldValue(
            'my_favorites_page',
            selectedResident.myFavoritesPage,
          );
          actions.setFieldValue(
            'person2_drupal_uuid',
            selectedResident.person2DrupalUuid ?? '',
          );
          actions.setFieldValue(
            'person2_first_name',
            selectedResident.person2FirstName ?? '',
          );
          actions.setFieldValue(
            'person2_last_name',
            selectedResident.person2LastName ?? '',
          );
          actions.setFieldValue(
            'person2_email',
            selectedResident.person2Email ?? '',
          );
        }
      }

      // Advance to last form step.
      setCurrentStep(currentStep + 2);
      actions.setTouched({});
      actions.setSubmitting(false);
    } else {
      // If the CRM is Enquire or Sherpa, they aren't allowed to edit the person.
      // Welcome Home allows us to edit, but I'm moving that functionality to the
      // next release.
      // Start the personalization session.
      const selectedResident = crmData[values.residentSelection];
      if (selectedResident) {
        values.drupal_uuid = selectedResident.drupalUuid;
        values.first_name = selectedResident.firstName ?? '';
        values.last_name = selectedResident.lastName ?? '';
        values.email = selectedResident.email ?? '';
        values.my_favorites_page = selectedResident.myFavoritesPage ?? '';
        values.person2_drupal_uuid = selectedResident.person2drupalUuid;
        values.person2_crm_individual_id = selectedResident.person2firstName ?? '';
        values.person2_first_name = selectedResident.person2FirstName ?? '';
        values.person2_last_name = selectedResident.person2LastName ?? '';
        values.person2_email = selectedResident.person2Email ?? '';
        values.favorites_page_message = selectedResident.favoritesPageMessage;
      }

      let formIntent = '';
      // Find out if the person selected has already been added to Drupal.
      // The CRM id first, then email plus both names — see
      // findExistingResidentInDrupal(). Matching on the CRM id alone missed
      // every lead converted from a chatbot session at a community whose CRM
      // defers its create, because those Residents were left with the field
      // empty, and Personalization then posted a second node for the same
      // person on top of their existing history.
      let person1Matched = false;

      if (selectedResident) {
        const person1Match = await findExistingResidentInDrupal(
          selectedResident,
          communityNid,
        );

        if (person1Match) {
          // Add Drupal id to selected resident.
          selectedResident.drupalUuid = person1Match.drupalUuid;
          selectedResident.crmIndividualId = person1Match.crmIndividualId;
          selectedResident.crmProspectId = person1Match.crmProspectId;
          selectedResident.drupalNid = person1Match.drupalNid;
          selectedResident.yourTourSessionsUuid = person1Match.yourTourSessionsUuid;
          selectedResident.favoriteAmenities = person1Match.favoriteAmenities;
          selectedResident.favoriteFloorPlans = person1Match.favoriteFloorPlans;
          selectedResident.favoriteMedia = person1Match.favoriteMedia;
          selectedResident.favoriteResidences = person1Match.favoriteResidences;
          selectedResident.myFavoritesPage = person1Match.myFavoritesPage;
          values.drupal_uuid = person1Match.drupalUuid;
          values.drupal_nid = person1Match.drupalNid;
          person1Matched = true;
        }
      }
      // CRM person not found in Drupal.
      if (!person1Matched) {
        formIntent = 'New Resident(s)';
      } else {
        formIntent = 'No Changes';
        // WE AREN'T UPDATING ENQUIRE OR SHERPA DATA RIGHT NOW
        // check if person 1 has changed from incoming crm info from drupal, not enquire
        // let compareData = compareIncomingCrmDataToFormValues(person1MatchData, values);
        // formIntent = compareData.formIntent;
        // updatedFormData = compareData.updatedFormData;
      }

      submitPersonalization(values, {}, formIntent, selectedResident);
    }
  }

  /**
   * Step 3 submit.
   *
   * @param {Object} actions
   */
  const step3Submit = (actions) => {
    // Advance to last form step.
    setCurrentStep(currentStep + 1);
    actions.setTouched({});
    actions.setSubmitting(false);
  };

  /**
   * Step 4 submit.
   *
   * @param {Array} values
   */
  async function step4Submit(incomingValues) {
    const values = incomingValues;

    // Disable button until data is sent from form to Drupal and CRM

    // If we're on the 2nd step (final step), do final submission which
    // begins session.
    let updatedFormData = {};

    // Track form intent. Either 'New Resident' or 'Update Resident'.
    let formIntent = '';

    let selectedResident = null;

    // If `residentSelected` is true, then a resident was found from the CRM.
    if (residentSelected !== '') {
      // Get the resident object based on the user selection of residents
      // from the crm.
      selectedResident = crmData[residentSelected];
      // Community does not use Drupal as CRM
      if (machineName !== 'drupal') {
        // Find out if the person selected has already been added to Drupal —
        // CRM id first, then email plus both names. Same reasoning as step 2
        // above; see findExistingResidentInDrupal().
        let person1Matched = false;

        if (selectedResident) {
          const person1DrupalMatch = await findExistingResidentInDrupal(
            selectedResident,
            communityNid,
          );

          if (person1DrupalMatch) {
            // Add drupal id to selected resident.
            selectedResident.drupalUuid = person1DrupalMatch.drupalUuid;
            selectedResident.drupalNid = person1DrupalMatch.drupalNid;
            selectedResident.yourTourSessions = person1DrupalMatch.yourTourSessions;
            selectedResident.favoriteAmenities = person1DrupalMatch.favoriteAmenities;
            selectedResident.favoriteFloorPlans = person1DrupalMatch.favoriteFloorPlans;
            selectedResident.favoriteMedia = person1DrupalMatch.favoriteMedia;
            selectedResident.favoriteResidences = person1DrupalMatch.favoriteResidences;
            selectedResident.myFavoritesPage = person1DrupalMatch.myFavoritesPage;
            values.drupal_uuid = person1DrupalMatch.drupalUuid;
            values.drupal_nid = person1DrupalMatch.drupalNid;
            person1Matched = true;
          }
        }

        // CRM person is not in Drupal.
        if (!person1Matched) {
          formIntent = 'New Resident(s)';
        } else {
          formIntent = 'No Changes';
          // WE AREN'T UPDATING ENQUIRE DATA RIGHT NOW
          // Check if person 1 has changed from incoming crm info from Drupal, not Enquire.
          // let compareData = compareIncomingCrmDataToFormValues(person1MatchData, values);
          // formIntent = compareData.formIntent;
          // updatedFormData = compareData.updatedFormData;
        }
      } else {
        // Drupal is the CRM.
        const compareData = compareIncomingCrmDataToFormValues(
          selectedResident,
          values,
        );
        formIntent = compareData.formIntent;
        updatedFormData = compareData.updatedFormData;
      }
    } else {
      // If the user got a result but they've decided to make a new person, we
      // need to reset this crmDataState or else the new person won't be submitted.
      setCrmDataState('notFound');
      formIntent = 'New Resident(s)';
      values.resident = '';
    }

    submitPersonalization(
      values,
      updatedFormData,
      formIntent,
      selectedResident,
    );
  }

  const updateSessionsDataAndPatchResidentInCRM = async (
    residentCookieData,
    favoritesCookieData,
    residenceFavoritesCookieData,
    mediaFavoritesCookieData,
  ) => {
    // Update session data.
    const residentCookie = cookies.get('resident');
    const now = new Date();
    const iso = toIsoString(now);
    residentCookie.yourtour_active_session.end_value = iso;

    const attributesData = await combineAndFormatYtSessionsForDrupal(
      residentCookie,
    );

    // We update non-Drupal favorites at the end of the session so we don't live post
    // changes in favorites and session times.
    const relationshipsData = {};
    if (machineName !== 'drupal') {
      // Assemble non-media favorites.

      const fpFavorites = favoritesCookieData.favorites.floor_plan;
      const fpFavoriteRelationships = [];
      if (favoritesCookieData.favorites?.floor_plan) {
        // eslint-disable-next-line no-restricted-syntax
        for (const fpFavorite of fpFavorites) {
          const fpFavoriteRelationship = {
            type: 'node--floor_plan',
            id: fpFavorite,
          };
          fpFavoriteRelationships.push(fpFavoriteRelationship);
        }
      }

      relationshipsData.field_favorite_floor_plans = {
        data: fpFavoriteRelationships,
      };

      const pFavorites = favoritesCookieData.favorites.place;
      const pFavoriteRelationships = [];
      if (favoritesCookieData.favorites?.place) {
        // eslint-disable-next-line no-restricted-syntax
        for (const pFavorite of pFavorites) {
          const pFavoriteRelationship = {
            type: 'node--place',
            id: pFavorite,
          };
          pFavoriteRelationships.push(pFavoriteRelationship);
        }
      }

      relationshipsData.field_favorite_amenities = {
        data: pFavoriteRelationships,
      };

      const rFavorites = residenceFavoritesCookieData?.favorites?.residence;
      const rFavoriteRelationships = [];
      if (residenceFavoritesCookieData?.favorites?.residence) {
        // eslint-disable-next-line no-restricted-syntax
        for (const rFavorite of rFavorites) {
          const rFavoriteRelationship = {
            type: 'node--residence',
            id: rFavorite,
          };
          rFavoriteRelationships.push(rFavoriteRelationship);
        }
      }

      relationshipsData.field_favorite_residences = {
        data: rFavoriteRelationships,
      };

      // Assemble media favorites.

      const mFavoriteRelationships = [];

      const vFavorites = mediaFavoritesCookieData?.favorites?.video;
      if (vFavorites) {
        // eslint-disable-next-line no-restricted-syntax
        for (const vFavorite of vFavorites) {
          const vFavoriteRelationship = {
            type: 'node--video',
            id: vFavorite,
          };
          mFavoriteRelationships.push(vFavoriteRelationship);
        }
      }

      const vtFavorites = mediaFavoritesCookieData?.favorites?.virtual_tour;
      if (vtFavorites) {
        // eslint-disable-next-line no-restricted-syntax
        for (const vtFavorite of vtFavorites) {
          const vtFavoriteRelationship = {
            type: 'node--virtual_tour',
            id: vtFavorite,
          };
          mFavoriteRelationships.push(vtFavoriteRelationship);
        }
      }

      const pgFavorites = mediaFavoritesCookieData?.favorites?.photo_gallery;
      if (pgFavorites) {
        // eslint-disable-next-line no-restricted-syntax
        for (const pgFavorite of pgFavorites) {
          const pgFavoriteRelationship = {
            type: 'node--photo_gallery',
            id: pgFavorite,
          };
          mFavoriteRelationships.push(pgFavoriteRelationship);
        }
      }

      relationshipsData.field_favorite_media = {
        data: mFavoriteRelationships,
      };
    }

    const response = await patchResidentInDrupal(
      residentCookieData.drupal_uuid,
      attributesData,
      null,
      relationshipsData,
    );

    // fetchWithAuthentication resolves with the parsed body whatever the
    // status, so an HTTP error arrives here as JSON:API `errors` rather than
    // as a throw. Only a body carrying the saved resident means it landed.
    if (!response?.data) {
      const error = response?.errors?.[0];
      throw new Error(`Resident save failed: ${error?.status ?? 'no resident'} ${error?.title ?? ''}`);
    }

    return response;
  };

  /**
   * End personalization warning submit.
   */
  async function endPersonalizationWarningSubmit() {
    setIsUpdatingProspect(true);
    setEndSessionSaveError(null);
    const residentCookieData = cookies.get('resident');
    const favoritesCookieData = cookies.get('favorites');
    const residenceFavoritesCookieData = cookies.get('residence_favorites');
    const mediaFavoritesCookieData = cookies.get('media_favorites');

    // Save cookie data for session ended modal.
    setCookieDataDump(residentCookieData);

    try {
      // Update sessions data and send all actions back to Drupal.
      await updateSessionsDataAndPatchResidentInCRM(
        residentCookieData,
        favoritesCookieData,
        residenceFavoritesCookieData,
        mediaFavoritesCookieData,
      );
    } catch (error) {
      // The save didn't land, so the cookies are the only copy of this
      // session. Leave them and the warning step in place so ending can be
      // retried.
      // eslint-disable-next-line no-console
      console.error('Unable to save the personalization session.', error);
      setEndSessionSaveError(
        'This session could not be saved, so it has not ended. Please try again.',
      );
      setIsUpdatingProspect(false);
      return;
    }

    // The save above is what makes Drupal push to the CRM, so only now is the
    // outcome knowable. JSON:API gives it nowhere to report a failure, so ask
    // for it separately and show it on the session-ended screen.
    try {
      setCrmPushStatus(
        await fetchCrmPushStatus(urlSlug, [
          residentCookieData?.drupal_uuid,
          residentCookieData?.person2_drupal_uuid,
        ]),
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Unable to fetch the CRM push status.', error);
      // The save has landed, so the session still ends. But "nothing to
      // report" would read as a successful push, which is unknown here.
      setCrmPushStatus(crmPushStatusUnknown(residentCookieData));
    } finally {
      setIsUpdatingProspect(false);
    }

    removeYtCookies(cookies);
    syncFavoritesFromCookies();
    setHasCRMResults(false);
    setPersonalizationActive(false);
    setCurrentStep('endPersonalization');
  }

  const [welcomeMessageSaved, setWelcomeMessageSaved] = useState(false);

  const triggerWelcomeMessage = () => {
    // Ensure we have the necessary data
    if (cookieDataDump) {
      setCurrentStep('personalizeWelcomeMessage');
      updateModalSize('smallMedium');
    } else {
      console.error('No resident data found. Unable to show welcome message.');
      // Optionally, you could redirect to the first step of personalization here
      // setCurrentStep(1);
    }
  };

  const handleGoBack = (messageSaved = false) => {
    setWelcomeMessageSaved(messageSaved);
    setCurrentStep('endPersonalization');
    updateModalSize('small');
  };

  /**
   * Form submit handler that is fired as the user goes between the form steps.
   *
   * @param {Array} values
   * @param {Object} actions
   */
  async function handleSubmit(values, actions) {
    trimSubmittedValues(values, actions);

    switch (currentStep) {
      case 1:
        // If we're on step 1 when the form is submitted, submit the user entered
        // to CRM and retrieve resident info.
        await step1Submit(values, actions);
        break;
      case 2:
        await step2Submit(values, actions);
        break;
      case 3:
        step3Submit(actions);
        break;
      case 4:
        setDataIsSent(false);
        await step4Submit(values);
        setDataIsSent(true);
        break;
      case 'endPersonalizationWarning':
        await endPersonalizationWarningSubmit();
        break;
      case 'personalizeWelcomeMessage':
        closeModal(); // Close the modal after the welcome message step
        break;
      default:
    }
  }

  // Shared cross-step nav buttons, dropped into each step's footer so they
  // share that step's modal__btns container with its action button(s).
  const footerNav = (
    <FormNavButtons
      currentStep={currentStep}
      goBackStep={goBackStep}
      cancelPersonalization={cancelPersonalization}
      closeModal={closeModal}
      isUpdatingProspect={isUpdatingProspect}
    />
  );

  return (
    <Formik
      // Initial Form Values.
      initialValues={{
        drupal_uuid: '',
        email: '',
        first_name: '',
        last_name: '',
        my_favorites_page: '',
        person2_drupal_uuid: '',
        person2_first_name: '',
        person2_last_name: '',
        person2_email: '',
        residentSelection: '',
      }}
      validationSchema={currentValidationSchema}
      // Formik owns this prop; there is nowhere to hoist the handler to.
      // eslint-disable-next-line react/jsx-no-bind
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit: submitForm,
        isSubmitting,
        setFieldValue,
      }) => (
        <form className="personalization-form modal__section" onSubmit={submitForm}>
          {/* Based on step that we are on the form, render the correct form component. */}
          {/* User enteres first name, last name, and/or email. */}
          {currentStep === 1 && (
            <AccountLookupStep
              values={values}
              errors={errors}
              touched={touched}
              handleChange={handleChange}
              handleBlur={handleBlur}
              isSubmitting={isSubmitting}
              setHasCRMResults={setHasCRMResults}
              footerNav={footerNav}
            />
          )}

          {/* Matches are returned and user must select user or make a new one. */}
          {currentStep === 2 && (
            <AccountResidentSelectionStep
              residentSelected={residentSelected}
              setResidentSelected={setResidentSelected}
              values={values}
              errors={errors}
              touched={touched}
              handleChange={handleChange}
              handleBlur={handleBlur}
              isSubmitting={isSubmitting}
              setFieldValue={setFieldValue}
              crmData={crmData}
              setCurrentStep={setCurrentStep}
              footerNav={footerNav}
            />
          )}

          {currentStep === 3 && (
            <AccountNotFoundStep
              values={values}
              errors={errors}
              touched={touched}
              handleChange={handleChange}
              handleBlur={handleBlur}
              crmDataState={crmDataState}
              isSubmitting={isSubmitting}
              setFieldValue={setFieldValue}
              setCurrentStep={setCurrentStep}
              footerNav={footerNav}
            />
          )}

          {currentStep === 4 && (
            <AccountFoundStep
              values={values}
              errors={errors}
              touched={touched}
              handleChange={handleChange}
              handleBlur={handleBlur}
              crmDataState={crmDataState}
              isSubmitting={isSubmitting}
              setFieldValue={setFieldValue}
              setCurrentStep={setCurrentStep}
              dataIsSent={dataIsSent}
              footerNav={footerNav}
            />
          )}

          {currentStep === 'endPersonalizationWarning' && (
            <EndPersonalizationWarning
              isSubmitting={isSubmitting}
              isUpdatingProspect={isUpdatingProspect}
              saveError={endSessionSaveError}
              closeModal={closeModal}
            />
          )}

          {currentStep === 'endPersonalization' && (
            <EndPersonalization
              cookieDataDump={cookieDataDump}
              triggerWelcomeMessage={triggerWelcomeMessage}
              welcomeMessageSaved={welcomeMessageSaved}
              crmPushStatus={crmPushStatus}
              closeModal={closeModal}
            />
          )}

          {currentStep === 'personalizeWelcomeMessage' && (
            <PersonalizeWelcomeMessage
              cookieDataDump={cookieDataDump}
              isSubmitting={isSubmitting}
              handleGoBack={handleGoBack}
              onSaveWelcomeMessage={() => {
                handleGoBack(true);
              }}
              residentUuid={cookieDataDump.drupal_uuid}
            />
          )}
        </form>
      )}
    </Formik>
  );
};

PersonalizationForm.propTypes = {
  beginSession: PropTypes.func.isRequired,
  updateModalSize: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  currentStep: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  setCurrentStep: PropTypes.func.isRequired,
  setHasCRMResults: PropTypes.func.isRequired,
  cookieDataDump: personalizationDataShape,
  setCookieDataDump: PropTypes.func.isRequired,
  setPersonalizationActive: PropTypes.func.isRequired,
  isUpdatingProspect: PropTypes.bool.isRequired,
  setIsUpdatingProspect: PropTypes.func.isRequired,
  goBackStep: PropTypes.func.isRequired,
  cancelPersonalization: PropTypes.func.isRequired,
};

PersonalizationForm.defaultProps = {
  cookieDataDump: null,
};

export default PersonalizationForm;
